import './Footer.scss';

import { type FC, useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import useFontFamiliesQuery, {
  FontFamiliesQuery,
} from 'queries/useFontFamiliesQuery';
import * as Core from 'components/core';
import ColorBar from 'components/ColorBar';

interface Props {}

export const Footer: FC<Props> = () => {
  const { t } = useTranslation();
  const { data } = useFontFamiliesQuery();

  const [fontFamilies, conceptFamilies] = useMemo(() => {
    if (data?.fontFamilies == null) {
      return [[], []];
    }

    const sortedFamilies = [...data.fontFamilies].sort((familyA, familyB) => {
      return familyA.name > familyB.name ? 1 : -1;
    });

    return sortedFamilies.reduce(
      (result, family) => {
        result[family.conceptFont ? 1 : 0].push(family);
        return result;
      },
      [[], []] as [
        FontFamiliesQuery['fontFamilies'],
        FontFamiliesQuery['fontFamilies'],
      ],
    );
  }, [data]);

  return (
    <ColorBar>
      <footer className="content-wrapper">
        <div className="Footer">
          <div className="Footer-nav">
            <div className="Footer-nav-group">
              <div className="Footer-nav-group-label">
                {t('footer.contact')}
              </div>
              <ul className="Footer-nav-group-list">
                <li className="Footer-nav-group-list-item">
                  <a
                    className="Footer-nav-group-link"
                    href="mailto:info@kilotype.de"
                  >
                    Email
                  </a>
                </li>
                <li className="Footer-nav-group-list-item">
                  <a
                    className="Footer-nav-group-link"
                    href="https://www.instagram.com/kilo.type/"
                  >
                    Instagram
                  </a>
                </li>
                <li className="Footer-nav-group-list-item">
                  <a
                    className="Footer-nav-group-link"
                    href="https://twitter.com/kilotype"
                  >
                    Twitter
                  </a>
                </li>
              </ul>
            </div>

            <div className="Footer-nav-group">
              <div className="Footer-nav-group-label">{t('footer.info')}</div>
              <ul className="Footer-nav-group-list">
                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/about">
                    {t('pages.about')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/faq">
                    {t('pages.frequentlyAskedQuestions')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/account">
                    {t('pages.account')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link
                    className="Footer-nav-group-link"
                    to="/our-fonts-in-use"
                  >
                    {t('pages.ourFontsInUse')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/licensing">
                    {t('pages.licensing')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link
                    className="Footer-nav-group-link"
                    to="/terms-and-conditions"
                  >
                    {t('pages.termsAndConditions')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/privacy-policy">
                    {t('pages.privacyPolicy')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/imprint">
                    {t('pages.imprint')}
                  </Link>
                </li>

                <li className="Footer-nav-group-list-item">
                  <Link className="Footer-nav-group-link" to="/returns-policy">
                    {t('pages.returnsPolicy')}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="Footer-nav-group">
              <div className="Footer-nav-group-label">{t('footer.fonts')}</div>

              <ul className="Footer-nav-group-list">
                {fontFamilies.map((fontFamily) => (
                  <li
                    className="Footer-nav-group-list-item"
                    key={fontFamily.id}
                  >
                    <Link
                      className="Footer-nav-group-link"
                      to={`/families/${fontFamily.slug}`}
                    >
                      {fontFamily.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {conceptFamilies.length > 0 && (
              <div className="Footer-nav-group">
                <div className="Footer-nav-group-label">
                  {t('footer.concepts')}
                </div>

                <ul className="Footer-nav-group-list">
                  {conceptFamilies.map((fontFamily) => (
                    <li
                      className="Footer-nav-group-list-item"
                      key={fontFamily.id}
                    >
                      <Link
                        className="Footer-nav-group-link"
                        to={`/families/${fontFamily.slug}`}
                      >
                        {fontFamily.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="Footer-newsletter">
            <div className="Footer-nav-group-label">
              {t('footer.newsletter')}
            </div>

            <form
              className="Footer-group-newsletter js-cm-form"
              action="https://www.createsend.com/t/subscribeerror?description="
              method="post"
              data-id="A61C50BEC994754B1D79C5819EC1255C15B4E1A20319A6020078E5C64AF1AEB416CACDB6BF5F0A60D601952F286D5B78D017EBF29A1D3A9F877F87F7DF29E35D"
            >
              <Core.Input
                required
                type="email"
                name="cm-wiyxh-wiyxh"
                placeholder="email address"
                className="js-cm-email-input"
              />

              <Core.Button type="submit">
                {t('footer.newsletterSubscribe')}
              </Core.Button>
            </form>
          </div>
        </div>

        <div className="Footer-copyright">
          {t('footer.copyright', { year: new Date().getFullYear() })}
        </div>
      </footer>
    </ColorBar>
  );
};

export default Footer;
